import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import { Box, CircularProgress, Container, Paper, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import { BidAutomationRules } from '~/components/GoogleAds/BidAutomationRules';
import HoverNav from '~/components/GoogleAds/HoverNav';

import { AdGroupDetails, getAdGroup } from '~/services/PublicOfferFeedService';

const getBiddingStrategyInfo = (overview: AdGroupDetails) => {
  const strategyType = overview?.bidding_strategy_type;

  if (!strategyType) return null;

  const strategyConfigs = {
    TARGET_ROAS: {
      heading: 'Target ROAS',
      value: overview?.troas > 0 ? `${overview.troas} %` : null,
    },
    MAXIMIZE_CONVERSION_VALUE: {
      heading: 'Target ROAS',
      value: overview?.troas > 0 ? `${overview.troas} %` : null,
    },
    TARGET_CPA: {
      heading: 'Target CPA',
      value: overview?.tcpa > 0 ? overview.tcpa : null,
    },
    MAXIMIZE_CONVERSIONS: {
      heading: 'Target CPA',
      value: overview?.tcpa > 0 ? overview.tcpa : null,
    },
    PERCENT_CPC: {
      heading: 'CPC %',
      value: overview?.tcpc_percentage > 0 ? `${overview.tcpc_percentage} %` : null,
    },
    MANUAL_CPC: {
      heading: 'Target CPC',
      value: overview?.tcpc > 0 ? `$ ${overview.tcpc}` : null,
    },
  };

  return strategyConfigs[strategyType] || null;
};

const BiddingStrategyDisplay = ({ overview }: { overview: AdGroupDetails }) => {
  const strategyInfo = getBiddingStrategyInfo(overview);

  if (!strategyInfo || !strategyInfo.value) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
        {strategyInfo.heading}
      </Typography>
      <Typography fontSize={16}> {strategyInfo.value}</Typography>
    </Box>
  );
};

function AdGroupDetailsView() {
  const { customerId, campaignId, adGroupId } = useParams<{
    customerId: string;
    campaignId: string;
    adGroupId: string;
  }>();

  const [fetchingState, setFetchingState] = useState<Utils.FetchingState>('idle');
  const [overview, setOverview] = useState<AdGroupDetails>(null);
  const { enqueueSnackbar } = useSnackbar();

  const fetchData = useCallback(
    async (customerId: string, campaignId: string, adGroupId: string) => {
      try {
        setFetchingState('loading');
        const { result } = await getAdGroup(customerId, campaignId, adGroupId);
        setOverview(result);
        setFetchingState('success');
      } catch (error) {
        setFetchingState('failed');
        enqueueSnackbar('Failed to fetch data', { variant: 'error' });
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    fetchData(customerId, campaignId, adGroupId);
  }, []);

  if (fetchingState === 'loading') {
    return (
      <Box p={2} display="flex" justifyContent="center">
        <CircularProgress variant="indeterminate" />
      </Box>
    );
  }

  return (
    <div className="offer-deal with-side-bar">
      <div className="side-bar">
        <HoverNav />
      </div>
      <Container maxWidth="xl">
        <PageSubheader title={overview?.ad_group_name}>
          <Box>
            <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
              {overview?.status}
            </Typography>
          </Box>
        </PageSubheader>

        <Paper sx={{ my: 4 }} id="overview" className="branded-panel">
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 8,
                lg: 9,
              }}
            >
              <Stack direction="column" spacing={2}>
                <Box>
                  <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                    Ad Group Id
                  </Typography>
                  <Typography fontSize={16}> {overview?.ad_group_id}</Typography>
                </Box>

                <Box>
                  <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                    Property
                  </Typography>
                  <Typography fontSize={16}>{overview?.hotel}</Typography>
                </Box>

                <Box>
                  <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                    Campaign Name
                  </Typography>
                  <Typography fontSize={16}> {overview?.campaign_name}</Typography>
                </Box>
                {overview?.campaign_start_date && (
                  <Box>
                    <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                      Campaign Start Date
                    </Typography>
                    <Typography fontSize={16}> {overview?.campaign_start_date}</Typography>
                  </Box>
                )}
                {overview?.campaign_end_date && (
                  <Box>
                    <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                      Campaign End Date
                    </Typography>
                    <Typography fontSize={16}> {overview?.campaign_end_date}</Typography>
                  </Box>
                )}

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h5" fontSize={18} fontWeight="bold" sx={{ mb: 2 }}>
                    Metrics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid
                      size={{
                        xs: 6,
                        md: 4,
                      }}
                    >
                      <Stack direction="column" spacing={2}>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Spend
                          </Typography>
                          <Typography fontSize={16}> ${overview?.spend}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Views
                          </Typography>
                          <Typography fontSize={16}> {overview?.views}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Margin
                          </Typography>
                          <Typography fontSize={16}> ${overview?.margin}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Clicks
                          </Typography>
                          <Typography fontSize={16}> {overview?.clicks}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Impressions
                          </Typography>
                          <Typography fontSize={16}> {overview?.impressions}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid
                      size={{
                        xs: 6,
                        md: 4,
                      }}
                    >
                      <Stack direction="column" spacing={2}>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            CPC
                          </Typography>
                          <Typography fontSize={16}> ${overview?.cpc}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            CTR
                          </Typography>
                          <Typography fontSize={16}> {overview?.ctr}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Margin per View
                          </Typography>
                          <Typography fontSize={16}> ${overview?.mpv}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Impression Share
                          </Typography>
                          <Typography fontSize={16}> {overview?.impression_share}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Margin ROAS
                          </Typography>
                          <Typography fontSize={16}> {overview?.mroas}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 4,
                      }}
                    >
                      <Stack direction="column" spacing={2}>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Conversion Value
                          </Typography>
                          <Typography fontSize={16}> ${overview?.conversion_value}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Conversions
                          </Typography>
                          <Typography fontSize={16}> {overview?.conversions}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Margin/View Change (3d vs 7d)
                          </Typography>
                          <Typography fontSize={16}> {overview?.margin_per_view_change_3d_vs_7d_pct}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Margin/View Change (1d vs 7d)
                          </Typography>
                          <Typography fontSize={16}> {overview?.margin_per_view_change_1d_vs_7d_pct}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Conversion Rate Change (3d vs 7d)
                          </Typography>
                          <Typography fontSize={16}> {overview?.conversion_rate_change_3d_vs_7d_pct}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Conversion Rate Change (1d vs 7d)
                          </Typography>
                          <Typography fontSize={16}> {overview?.conversion_rate_change_1d_vs_7d_pct}%</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4,
                lg: 3,
              }}
            >
              <Stack direction="column" spacing={2}>
                <Box>
                  <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                    Bidding Strategy
                  </Typography>
                  <Typography fontSize={16}> {overview?.bidding_strategy_type}</Typography>
                </Box>
                <BiddingStrategyDisplay overview={overview} />
              </Stack>
            </Grid>
          </Grid>
        </Paper>
        <BidAutomationRules
          campaignId={campaignId}
          adGroupId={adGroupId}
          customerId={customerId}
          fetchData={fetchData}
        />
      </Container>
    </div>
  );
}

export default AdGroupDetailsView;
